/* eslint-disable react-refresh/only-export-components */
import { useMutation, useQuery } from 'urql';
import { graphql } from '../../gql';
import React, { useEffect, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Button } from '@progress/kendo-react-buttons';
import { Grid, GridColumn, GridNoRecords } from '@progress/kendo-react-grid';
import { PageTitle } from '../PageTitle.tsx';
import { Fieldset } from '../form-components/Fieldset.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import { BackButton } from '../../shared/BackButton.tsx';
import { Link } from '@tanstack/react-router';
import { SignUpCustomField } from '../../gql/graphql.ts';
import { cloneAndRemoveTypename } from '../../public/util.ts';
import { InputStateWrapper } from '../form-components/InputStateWrapper.tsx';
import {
  CompanySignUpFormsQuery,
  UpdateSignUpFormMutation,
} from './SignUpFormsPage.tsx';

interface SignUpFormPageProps {
  companyId: string;
  formId: string;
}

export const SignUpFormElementsQuery = graphql(/* GraphQL */ `
  query companySignUpFormElements($companyId: String!, $formId: String) {
    companySignUpCustomFields(companyId: $companyId, formId: $formId) {
      id
      type
      btsClass
      btsName
      name
      required
      showAfter
      showInMail
      systemField
      type
      validator
      visibleIfField
      visibleIfValue
      options {
        displayValue
        value
      }
    }
  }
`);

export const UpdateSignUpFormElementsMutation = graphql(/* GraphQL */ `
  mutation updateSignUpFormElements(
    $signUpFormElements: [SignUpCustomFieldInput!]!
    $companyId: String!
    $formId: String
  ) {
    updateSignUpCustomFields(
      companyId: $companyId
      signUpCustomFields: $signUpFormElements
      formId: $formId
    )
  }
`);

export const SignUpFormPage: React.FC<SignUpFormPageProps> = ({
  companyId,
  formId,
}) => {
  const [{ data: formsData }, reloadForms] = useQuery({
    query: CompanySignUpFormsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const activeForm = formsData?.companySignUpForms.find((f) => f.id === formId);

  const [{ data, fetching }, reloadSeminars] = useQuery({
    query: SignUpFormElementsQuery,
    variables: { companyId, formId },
    pause: companyId === undefined || companyId === null,
  });

  const [, updatePublicSeminarPageFields] = useMutation(
    UpdateSignUpFormElementsMutation,
  );
  const [, updateSignUpForm] = useMutation(UpdateSignUpFormMutation);

  const { control, formState, handleSubmit, reset } = useForm<{
    name: string;
    elements: SignUpCustomField[];
  }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      elements: [],
    },
  });

  const loadedFormIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (fetching || !data?.companySignUpCustomFields || !activeForm) return;
    // On a form switch, always reset to the newly selected form's fields — even
    // if the form is dirty (those edits belonged to the previous form). Within
    // the same form, keep the dirty guard so a background refetch doesn't
    // overwrite in-progress edits.
    const formChanged = loadedFormIdRef.current !== formId;
    if (formChanged || !formState.isDirty) {
      loadedFormIdRef.current = formId;
      reset({
        name: activeForm.name,
        elements: cloneAndRemoveTypename(data.companySignUpCustomFields),
      });
    }
  }, [fetching, data, formId, activeForm]);

  const { fields, remove, move } = useFieldArray({
    control,
    name: 'elements',
    keyName: 'notUsed',
  });

  const { isValid, isDirty } = formState;

  const onFormSubmit = (value: {
    name: string;
    elements: SignUpCustomField[];
  }) => {
    const name = value.name.trim();
    Promise.all([
      updateSignUpForm({ companyId, formId, name }),
      updatePublicSeminarPageFields({
        companyId,
        formId,
        signUpFormElements: value.elements,
      }),
    ]).then(() => {
      reset({ ...value, name });
      reloadSeminars();
      // The name is part of the forms query (title, overview table) — the
      // mutation returns no typenames, so urql won't invalidate it by itself.
      reloadForms({ requestPolicy: 'network-only' });
    });
  };

  const pageTitle = activeForm
    ? `Anmeldeformular: ${activeForm.name}${activeForm.isDefault ? ' (Standard)' : ''}`
    : 'Anmeldeformular';

  return (
    <>
      <PageTitle title={pageTitle}>
        <BackButton href="/admin/sign-up-forms" />
      </PageTitle>

      <div className="mb-4 flex justify-end">
        <Link
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
          to={'/admin/sign-up-forms/$formId/fields/new'}
          params={{ formId }}
        >
          <HeroIcon name="Plus" className="h-5 w-5" />
          Neues Feld
        </Link>
      </div>

      <form
        onSubmit={handleSubmit(onFormSubmit)}
        className="k-form k-form-md relative flex w-full flex-col rounded-md bg-white shadow lg:w-full"
      >
        <Fieldset
          legend={`Formularname`}
          description="Name des Anmeldeformulars, wie er bei der Zuordnung zu Seminaren angezeigt wird."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <InputStateWrapper
              name={'name'}
              label={'Name'}
              control={control}
              rules={{ required: true }}
            />
          </div>
        </Fieldset>
        <Fieldset
          legend={`Formularfelder`}
          description="Hier kann der Aufbau und der Inhalt des Anmeldeformulars für Seminare angepasst werden."
        >
          <div className="flex flex-col gap-4 md:col-span-9 [&_.k-form-field]:!mt-0">
            <Grid
              className="[&_.k-grid-norecords-template]:border-0"
              scrollable={'scrollable'}
              data={fields}
              showLoader={fetching}
            >
              {!fetching && (
                <GridNoRecords>Keine Formularfelder festgelegt</GridNoRecords>
              )}
              <GridColumn
                title="Pos."
                width={'50px'}
                cells={{ data: (props) => <td>{props.dataIndex + 1}.</td> }}
              />
              <GridColumn
                title="Name"
                cells={{
                  data: (props) => <td>{fields[props.dataIndex].name}</td>,
                }}
              />
              <GridColumn
                title="BTS-Name"
                cells={{
                  data: (props) => <td>{fields[props.dataIndex].btsName}</td>,
                }}
              />
              <GridColumn
                title="Aktionen"
                width="220px"
                cells={{
                  data: (props) => (
                    <td className={'k-table-td'}>
                      <div className="k-button-link-secondary flex gap-1">
                        <Button
                          fillMode={'outline'}
                          type={'button'}
                          themeColor={'secondary'}
                          disabled={props.dataIndex === 0}
                          title="Feld nach oben verschieben"
                          onClick={() =>
                            move(props.dataIndex, props.dataIndex - 1)
                          }
                        >
                          <HeroIcon
                            name="ChevronUp"
                            className="block h-5 w-5"
                          />
                        </Button>
                        <Button
                          fillMode={'outline'}
                          type={'button'}
                          themeColor={'secondary'}
                          title="Feld nach unten verschieben"
                          onClick={() =>
                            move(props.dataIndex, props.dataIndex + 1)
                          }
                          disabled={props.dataIndex === fields.length - 1}
                        >
                          <HeroIcon
                            name="ChevronDown"
                            className="block h-5 w-5"
                          />
                        </Button>
                        <Link
                          to={'/admin/sign-up-forms/$formId/fields/edit/$id'}
                          params={{ formId, id: fields[props.dataIndex].id }}
                          className="k-button k-button-md k-rounded-md k-button-outline k-button-outline-secondary !text-[#6c757d] hover:!text-white"
                          title="Feld bearbeiten"
                        >
                          <HeroIcon name="Pencil" className="block h-5 w-5" />
                        </Link>
                        <Button
                          fillMode={'outline'}
                          type={'button'}
                          themeColor={'secondary'}
                          onClick={() => remove(props.dataIndex)}
                          title="Feld löschen"
                        >
                          <HeroIcon name="Trash" className="block h-5 w-5" />
                        </Button>
                      </div>
                    </td>
                  ),
                }}
              />
            </Grid>
          </div>
        </Fieldset>
        <div className="k-form-buttons !mt-0 justify-end rounded-md bg-gray-50 !px-8 !py-3 !text-right">
          <Button
            type={'submit'}
            themeColor={'primary'}
            className="!shadow-sm [&>span.k-button-text]:flex [&>span.k-button-text]:items-center [&>span.k-button-text]:gap-1"
            disabled={!isValid || !isDirty}
            title="Speichern"
          >
            <HeroIcon name="CheckCircle" className="block h-5 w-5" />
            <div>Speichern</div>
          </Button>
        </div>
      </form>
    </>
  );
};
